import { useCallback, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { ChatAppResponse } from "@/api/models"

import { PenLine } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { getResponseContent, getResponseContext } from "@/utils/response-format-utils"

import { deriveArtifactTitle } from "../utils/agent-artifacts-utils"

type UseAgentImproveActionOptions = {
  /** Live answers of the open thread, read at click time. */
  answersRef: React.RefObject<[string, ChatAppResponse][]>
  /** A turn is running, so the draft must not be replaced right now. */
  disabled: boolean
  insertDraft: (text: string) => void
}

/**
 * "Improve result" as a node for the answer's action row (#3165).
 *
 * `Chat.SystemMessage` is memoised, so the node is cached per message and only
 * rebuilt when its disabled state changes — handing it a fresh element on every
 * render would re-render every historical answer on every streamed token. The
 * answer itself is resolved at click time from `answersRef`, which keeps the
 * click handler stable too.
 */
export function useAgentImproveAction({
  answersRef,
  disabled,
  insertDraft,
}: UseAgentImproveActionOptions) {
  const { t } = useTranslation()
  const cacheRef = useRef(new Map<string, { disabled: boolean; node: React.ReactNode }>())
  return useCallback(
    (key: string, index: number): React.ReactNode => {
      const cached = cacheRef.current.get(key)
      if (cached && cached.disabled === disabled) return cached.node
      const node = (
        <Button
          className="h-7 px-2.5 text-xs"
          disabled={disabled}
          size="sm"
          variant="outline"
          onClick={() => {
            const answer = answersRef.current.at(index)
            if (!answer) return
            const content = getResponseContent(answer[1])
            insertDraft(
              t("agents.artifacts.refine_prompt", {
                title:
                  getResponseContext(answer[1]).artifact_candidate?.title ??
                  deriveArtifactTitle(content),
              })
            )
          }}
        >
          <PenLine aria-hidden="true" className="size-3.5" />
          {t("agents.artifacts.refine")}
        </Button>
      )
      cacheRef.current.set(key, { disabled, node })
      return node
    },
    [answersRef, disabled, insertDraft, t]
  )
}
